import { useEffect, useMemo, useRef, useState } from "react";
import type { DeviceLocation, ApLocation } from "../entities/DeviceLocation";
import { deviceService } from "@/services/deviceService";
import { datetimeStrToEpoch } from "@/utils/datetimeHelper";
import { MapControls } from "@/components/MapControls";
import * as localStorageHelper from "@/utils/localStorageHelper";
import { getDeviceLabel } from "@/utils/deviceName";

// v=beta required for google.maps.places.PlaceAutocompleteElement (not yet in the weekly/stable channel)
const MAPS_URL = `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_MAPS_API_KEY}&libraries=marker,places&v=beta`;
const GOOGLE_MAPS_MAP_ID = process.env.GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";
const THRESHOLD_MS = 3000; // 3 seconds threshold for considering IMU data as more recent than EKF
const FALLBACK_CENTER = { lat: 1.297575083270687, lng: 103.84943739782491 }; // SMU School of Computing and Information Systems 1
const DEFAULT_CENTER_STORAGE_KEY = "easyloc:defaultCenter";

/**
 * Resolves which location source (EKF or IMU) should represent a device right now,
 * mirroring the fallback rules applied when rendering its marker.
 */
function getEffectiveLocation(device: DeviceLocation) {
  let loc = device.ekf;

  const ekfDate = datetimeStrToEpoch(device.ekf.last_updated);
  const imuDate = datetimeStrToEpoch(device.imu.last_updated);

  if (device.ekf.lat === -999 || device.ekf.lng === -999) {
    loc = device.imu;
  }

  if (imuDate > ekfDate + THRESHOLD_MS) {
    loc = device.imu;
  }

  return loc;
}

function addZoomOnDoubleClick(
  el: HTMLElement,
  marker: google.maps.marker.AdvancedMarkerElement,
  mapRef: React.RefObject<google.maps.Map | null>,
) {
  el.addEventListener("dblclick", (e) => {
    e.stopPropagation();
    if (!marker.position) return;
    mapRef.current?.panTo(marker.position);
    mapRef.current?.setZoom(20);
  });
}

/**
 * Generates a deterministically chosen hex color from an arbitrary string.
 * This ensures that a device with a given UUID always receives the same visual representation on the map.
 *
 * @param str The string to convert into a color (typically the device UUID).
 * @returns A '#' prefixed hex color string.
 */
function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += value.toString(16).padStart(2, "0");
  }
  return color;
}

/**
 * A fast comparison function to see if two BSSIDs are functionally the same AP.
 * Ensures the first and last bytes differ by at most 4 bits and the middle 4 bytes match exactly.
 *
 * @param bssid1 The first BSSID.
 * @param bssid2 The second BSSID.
 * @returns true if they are deemed identical for mapping purposes, false otherwise.
 */
function isSameAP(bssid1: string, bssid2: string): boolean {
  if (!bssid1 || !bssid2) return false;

  const b1 = bssid1
    .toLowerCase()
    .split(":")
    .map((hex) => parseInt(hex, 16));
  const b2 = bssid2
    .toLowerCase()
    .split(":")
    .map((hex) => parseInt(hex, 16));

  // Guarantee both BSSIDs are valid 6-byte MAC addresses
  if (
    b1.length !== 6 ||
    b2.length !== 6 ||
    b1.some(Number.isNaN) ||
    b2.some(Number.isNaN)
  )
    return false;

  // Middle 4 bytes exact match
  for (let i = 1; i < 5; i++) {
    if (b1[i]! !== b2[i]!) return false;
  }

  // Helper macro to count difference in bits (Hamming weight of XOR)
  const diffBits = (n: number) => n.toString(2).split("1").length - 1;

  // First and last byte should only differ by 4 bits at most
  if (diffBits(b1[0]! ^ b2[0]!) > 4) return false;
  if (diffBits(b1[5]! ^ b2[5]!) > 4) return false;

  return true;
}

/**
 * Dashboard Component
 *
 * The main real-time tracking dashboard. It integrates Google Maps and real-time live device WebSocket feeds
 * to accurately display device coordinates and tracked Access Points (APs).
 */
export function Dashboard() {
  // State to hold current device locations from API
  const [deviceLocations, setDeviceLocations] = useState<DeviceLocation[]>([]);
  const [apLocations, setApLocations] = useState<any[]>([]);

  // State for error handling
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Selected floor for multi-floor sites; "all" shows every floor
  const [selectedFloor, setSelectedFloor] = useState<number | "all">("all");

  // UUID of the device the map should track ("follow me"); null = not following
  const [followedUuid, setFollowedUuid] = useState<string | null>(null);

  // Mirrors mapRef.current in state so <MapControls> re-renders once the map is ready
  const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);

  // Google Maps reference
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Using 'any' type for markers to avoid issues with Google Maps typings
  const deviceMarkersRef = useRef<Record<string, any>>({});
  const apMarkersRef = useRef<Record<string, any>>({});

  // Key ("device:<uuid>" / "ap:<bssid>") of the marker whose InfoWindow is currently open,
  // so clicking the same pin again (or the popup's "X") toggles it closed.
  const openMarkerKeyRef = useRef<string | null>(null);

  let isMapsLoaded = false; // Flag to prevent multiple map initializations

  // useEffect for WebSocket connection to receive real-time device location updates (on component mount)
  useEffect(() => {
    const ws = deviceService.connectWebSocket((devices) => {
      setDeviceLocations(devices);
      setApLocations(
        devices.find((d) => d.uuid === process.env.MASTER_UUID)?.aps || [],
      );
      setError(null);
    });

    return () => {
      ws.close();
    };
  }, []);

  // useEffect for initializing Google Maps (on component mount)
  useEffect(() => {
    if (isMapsLoaded == false) {
      isMapsLoaded = true;
    } else return;

    const initMap = async () => {
      try {
        function loadScript(src: string): Promise<HTMLScriptElement> {
          return new Promise((resolve, reject) => {
            // Create a new script element
            const script = document.createElement("script");
            script.src = src;
            script.async = true; // Load script asynchronously

            // Define success and error handlers
            script.onload = () => resolve(script);
            script.onerror = () =>
              reject(new Error(`Failed to load script: ${src}`));

            // Append the script to the document head to start loading
            document.head.appendChild(script);
          });
        }

        try {
          await loadScript(MAPS_URL);
          console.log(`Script loaded successfully: ${MAPS_URL}`);
        } catch (error) {
          console.error(error instanceof Error ? error.message : error);
          setError("Failed to load Google Maps script");
          return;
        }

        if (mapDivRef.current && !mapRef.current) {
          const { Map, RenderingType } = (await google.maps.importLibrary(
            "maps",
          )) as google.maps.MapsLibrary;

          mapRef.current = new Map(mapDivRef.current, {
            center: localStorageHelper.getItem(
              DEFAULT_CENTER_STORAGE_KEY,
              FALLBACK_CENTER,
            ),
            zoom: 16,
            mapId: GOOGLE_MAPS_MAP_ID,
            renderingType: RenderingType.VECTOR,
            scaleControl: true,
            streetViewControl: false,
          });

          // Initialize InfoWindow when map is created
          infoWindowRef.current = new google.maps.InfoWindow();
          // Dismissing the popup via its "X" clears the open-marker tracker
          infoWindowRef.current.addListener("closeclick", () => {
            openMarkerKeyRef.current = null;
          });
          setGoogleMap(mapRef.current);
        }
      } catch (error) {
        console.error("Error loading Google Maps:", error);
        setError("Failed to load Google Maps");
      }
    };

    initMap();
  }, []);

  // useEffect to update markers on the map whenever deviceLocations state changes
  useEffect(() => {
    async function updateMarkers() {
      if (!mapRef.current || deviceLocations.length === 0) {
        return;
      }

      try {
        // Import the marker library
        const { AdvancedMarkerElement, PinElement } =
          (await google.maps.importLibrary(
            "marker",
          )) as google.maps.MarkerLibrary;

        // const bounds = new google.maps.LatLngBounds();

        // Update or create markers for each device (only those on the selected floor, if one is chosen)
        const devicesOnFloor = deviceLocations.filter((device) => {
          if (selectedFloor === "all") return true;
          return getEffectiveLocation(device).floor === selectedFloor;
        });

        devicesOnFloor.forEach((device) => {
          const loc = getEffectiveLocation(device);

          if (!loc) {
            console.warn(`No location data for device ${device.uuid}`);
            return;
          }

          const position = {
            lat: loc.lat,
            lng: loc.lng,
          };

          // Check if marker already exists for this device
          if (deviceMarkersRef.current[device.uuid]) {
            // Update existing marker position
            deviceMarkersRef.current[device.uuid].position = position;
          } else {
            const deviceColor = stringToColor(device.uuid);

            const div = document.createElement("div");
            div.style.display = "flex";
            div.style.flexDirection = "column";
            div.style.alignItems = "center";
            div.style.gap = "2px";

            // Create new marker
            const pin = new PinElement({
              background: deviceColor,
              borderColor: deviceColor,
              glyphColor: "#ffffff",
              glyph: "D",
            });

            div.appendChild(pin.element);

            const label = document.createElement("div");
            label.textContent = getDeviceLabel(device);
            label.style.fontSize = "10px";
            label.style.fontWeight = "bold";
            label.style.backgroundColor = "white";
            label.style.padding = "1px 4px";
            label.style.borderRadius = "4px";
            label.style.border = "1px solid #ccc";
            div.appendChild(label);

            const marker = new AdvancedMarkerElement({
              map: mapRef.current,
              position: position,
              title: `Device ${device.uuid}`,
              content: div,
            });

            marker.addListener("click", () => {
              if (!infoWindowRef.current || !mapRef.current) return;

              const markerKey = `device:${device.uuid}`;

              // Clicking the same pin again closes its popup
              if (openMarkerKeyRef.current === markerKey) {
                infoWindowRef.current.close();
                openMarkerKeyRef.current = null;
                return;
              }

              infoWindowRef.current.close();
              // textContent (not innerHTML) since the device name is free text
              const popup = document.createElement("div");
              popup.style.cssText = "color: black; font-size: 14px; padding: 4px;";
              popup.textContent = `${getDeviceLabel(device)}\nUUID: ${device.uuid}`;
              popup.style.whiteSpace = "pre-line";

              infoWindowRef.current.setContent(popup);
              infoWindowRef.current.open(mapRef.current, marker);
              openMarkerKeyRef.current = markerKey;
            });

            addZoomOnDoubleClick(div, marker, mapRef);

            deviceMarkersRef.current[device.uuid] = marker;
          }

          // bounds.extend(position);
        });

        // Collect and aggregate all APs from all devices
        const aggregatedAPs = new Map<
          string,
          { ap: ApLocation; sourceDeviceUuid: string }
        >();

        deviceLocations.forEach((device) => {
          if (!device.aps || !Array.isArray(device.aps)) return;

          device.aps.forEach((ap: ApLocation) => {
            if (
              typeof ap.loc.lat !== "number" ||
              typeof ap.loc.lng !== "number" ||
              isNaN(ap.loc.lat) ||
              isNaN(ap.loc.lng)
            ) {
              return;
            }

            // Only show APs on the currently selected floor (AP floor is independent of the device's own floor)
            if (selectedFloor !== "all" && ap.loc.floor !== selectedFloor) {
              return;
            }

            // check if we already have this AP or a matching AP
            let existingKey: string | null = null;
            for (const key of aggregatedAPs.keys()) {
              if (isSameAP(key, ap.bssid)) {
                existingKey = key;
                break;
              }
            }

            if (existingKey) {
              const existingRecord = aggregatedAPs.get(existingKey)!;

              if (device.uuid === process.env.MASTER_UUID) {
                // Master overrides any existing records
                aggregatedAPs.set(existingKey, {
                  ap,
                  sourceDeviceUuid: device.uuid,
                });
              } else if (existingRecord.sourceDeviceUuid === device.uuid) {
                // Update our own record
                aggregatedAPs.set(existingKey, {
                  ap,
                  sourceDeviceUuid: device.uuid,
                });

                // If it's a different non-master device, first-come-first-serve, so do nothing.
                // TODO: Add some optimization to use the data from both devices instead of just one,
                // e.g. averaging the location or using the one with stronger signal strength.
              }
            } else {
              // Not found, add the new AP
              aggregatedAPs.set(ap.bssid, {
                ap,
                sourceDeviceUuid: device.uuid,
              });
            }
          });
        });

        // Create or update AP markers based on aggregated APs
        const currentApBssids = new Set<string>();

        aggregatedAPs.forEach((record, bssidKey) => {
          currentApBssids.add(bssidKey);
          const { ap } = record;

          const apPosition = {
            lat: ap.loc.lat,
            lng: ap.loc.lng,
          };

          // Check if AP marker already exists
          if (apMarkersRef.current[bssidKey]) {
            // Update existing AP marker position
            apMarkersRef.current[bssidKey].position = apPosition;
          } else {
            const bssidParts = ap.bssid.split(":");
            const cleanSsid = ap.ssid ? ap.ssid.replace(/^"|"$/g, "") : "";
            const ssidLastPart = cleanSsid
              ? cleanSsid.length > 5
                ? cleanSsid.slice(-5)
                : cleanSsid
              : "";
            const lastTwoBytes =
              bssidParts.length >= 2
                ? `${bssidParts[bssidParts.length - 2]}:${bssidParts[bssidParts.length - 1]}`
                : ap.bssid;

            const div = document.createElement("div");
            div.style.display = "flex";
            div.style.flexDirection = "column";
            div.style.alignItems = "center";
            div.style.gap = "2px";

            // Create new AP marker
            const apPin = new PinElement({
              background: "#1d4ed8",
              borderColor: "#1e40af",
              glyphColor: "#ffffff",
              glyph: "A",
            });

            div.appendChild(apPin.element);

            const label = document.createElement("div");
            label.textContent = ssidLastPart || lastTwoBytes;
            label.style.fontSize = "10px";
            label.style.fontWeight = "bold";
            label.style.backgroundColor = "white";
            label.style.padding = "1px 4px";
            label.style.borderRadius = "4px";
            label.style.border = "1px solid #ccc";
            div.appendChild(label);

            const apMarker = new AdvancedMarkerElement({
              map: mapRef.current,
              position: apPosition,
              title: ap.bssid, // Use original BSSID
              content: div,
            });

            apMarker.addListener("click", () => {
              if (!infoWindowRef.current || !mapRef.current) return;

              const markerKey = `ap:${bssidKey}`;

              // Clicking the same pin again closes its popup
              if (openMarkerKeyRef.current === markerKey) {
                infoWindowRef.current.close();
                openMarkerKeyRef.current = null;
                return;
              }

              infoWindowRef.current.close();
              infoWindowRef.current.setContent(
                `<div style="color: black; font-size: 14px; padding: 4px;">
                  <strong>SSID:</strong> ${ap.ssid || "Unknown"}<br/>
                  <strong>BSSID:</strong> ${ap.bssid}
                </div>`,
              );
              infoWindowRef.current.open(mapRef.current, apMarker);
              openMarkerKeyRef.current = markerKey;
            });

            addZoomOnDoubleClick(div, apMarker, mapRef);

            apMarkersRef.current[bssidKey] = apMarker;
          }
        });

        // Remove markers for APs that are no longer reported
        Object.keys(apMarkersRef.current).forEach((bssid) => {
          if (!currentApBssids.has(bssid)) {
            apMarkersRef.current[bssid].map = null;
            delete apMarkersRef.current[bssid];
            if (openMarkerKeyRef.current === `ap:${bssid}`) {
              infoWindowRef.current?.close();
              openMarkerKeyRef.current = null;
            }
          }
        });

        // Remove markers for devices that no longer exist or have moved off the selected floor
        const currentDeviceIds = new Set(devicesOnFloor.map((d) => d.uuid));

        Object.keys(deviceMarkersRef.current).forEach((uuid) => {
          if (!currentDeviceIds.has(uuid)) {
            deviceMarkersRef.current[uuid].map = null;
            delete deviceMarkersRef.current[uuid];
            if (openMarkerKeyRef.current === `device:${uuid}`) {
              infoWindowRef.current?.close();
              openMarkerKeyRef.current = null;
            }
          }
        });

        // // Fit bounds to show all markers
        // if (!bounds.isEmpty()) {
        //   mapRef.current.fitBounds(bounds, 80);
        // }
      } catch (error) {
        console.error("Error updating markers:", error);
      }
    }

    updateMarkers();
  }, [deviceLocations, selectedFloor]);

  // "Follow me": re-center the map on the followed device and switch to its floor
  // whenever it reports a new position.
  useEffect(() => {
    if (!followedUuid || !mapRef.current) return;

    const device = deviceLocations.find((d) => d.uuid === followedUuid);
    if (!device) return;

    const loc = getEffectiveLocation(device);
    if (!loc || loc.lat === -999 || loc.lng === -999) return;

    setSelectedFloor(loc.floor);
    mapRef.current.panTo({ lat: loc.lat, lng: loc.lng });
  }, [deviceLocations, followedUuid]);

  // Persists the map's current center as the default so future loads on this device/browser start there
  const handleSetDefaultCenter = () => {
    const center = mapRef.current?.getCenter();
    if (!center) return;

    localStorageHelper.setItem(DEFAULT_CENTER_STORAGE_KEY, {
      lat: center.lat(),
      lng: center.lng(),
    });
  };

  // Distinct floor numbers seen across device EKF/IMU locations and AP locations, for the floor picker
  const floors = useMemo(() => {
    const floorSet = new Set<number>();

    deviceLocations.forEach((device) => {
      floorSet.add(getEffectiveLocation(device).floor);
      device.aps?.forEach((ap) => floorSet.add(ap.loc.floor));
    });

    return Array.from(floorSet).sort((a, b) => a - b);
  }, [deviceLocations]);

  // Devices currently reporting, for the "follow me" picker
  const pickerDevices = useMemo(
    () => deviceLocations.map((d) => ({ uuid: d.uuid, label: getDeviceLabel(d) })),
    [deviceLocations],
  );

  return (
    <div className="w-full h-[90vh] relative">
      <div id="map" ref={mapDivRef} className="w-full h-full" />
      <MapControls
        map={googleMap}
        floors={floors}
        selectedFloor={selectedFloor}
        onFloorChange={setSelectedFloor}
        onSetDefaultCenter={handleSetDefaultCenter}
        devices={pickerDevices}
        followedUuid={followedUuid}
        onFollowChange={setFollowedUuid}
      />
    </div>
  );
}
